import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './LandingPage';
import PrivacyPolicy from './PrivacyPolicy';
import BlogIndex from './blog/BlogIndex';
import BlogPost from './blog/BlogPost';
import HQLogin from './hq/HQLogin';
import HQDashboard from './hq/HQDashboard';
import HQTransactions from './hq/HQTransactions';
import HQInsights from './hq/HQInsights';
import HQNotifications from './hq/HQNotifications';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Simple PrivateRoute wrapper
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();
  return currentUser ? <>{children}</> : <Navigate to="/hq" />;
}

/**
 * HQ admin area. Firebase auth (and its `loading` gate) is scoped to this
 * subtree only, so the public marketing routes (`/`, `/privacy-policy`) render
 * without waiting on `onAuthStateChanged`. That keeps their first client render
 * identical to the build-time prerender (see `scripts/prerender.mjs`) so React
 * can hydrate them cleanly instead of throwing the markup away.
 *
 * All HQ links/navigation use absolute `/hq/*` paths, so these nested routes are
 * declared relative to the `/hq` splat.
 */
function HQArea() {
  // Keep the admin area out of search indexes and Google Analytics, including
  // when the user client-side navigates into /hq from a public page.
  useEffect(() => {
    const gaKey = 'ga-disable-G-HVVG0G23PW';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive';
    document.head.appendChild(meta);
    (window as any)[gaKey] = true;
    return () => {
      meta.remove();
      delete (window as any)[gaKey];
    };
  }, []);

  return (
    <AuthProvider>
      <Routes>
        <Route path="" element={<HQLogin />} />
        <Route path="dashboard" element={<PrivateRoute><HQDashboard /></PrivateRoute>} />
        <Route path="transactions" element={<PrivateRoute><HQTransactions /></PrivateRoute>} />
        <Route path="insights" element={<PrivateRoute><HQInsights /></PrivateRoute>} />
        <Route path="notifications" element={<PrivateRoute><HQNotifications /></PrivateRoute>} />
      </Routes>
    </AuthProvider>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/hq/*" element={<HQArea />} />
      </Routes>
    </Router>
  );
}

export default App;
